// src/components/BuildingViews/AnvilView.js
import React, { useState } from 'react';
import styles from './AnvilView.module.css';
import { ItemData } from '../../data/ItemData';
import { getItemImageSrc } from '../../utils/getItemImageSrc';

const AnvilView = ({ playerData, onPlayerAction, onGoBack }) => {
  const [activeTab, setActiveTab] = useState('repair'); // 'repair' or 'upgrade'
  
  // Get current crafting status
  const activeCrafting = playerData?.buildings?.anvil?.activeCrafting;

  // Get repairable items (items with durability < maxDurability)
  const getRepairableItems = () => {
    const repairable = [];
    
    for (const [itemId, item] of Object.entries(playerData.inventory || {})) {
      if (typeof item === 'object' && item !== null && item.durability !== undefined) {
        if (item.durability < item.maxDurability) {
          const baseData = ItemData[item.key];
          repairable.push({
            ...item,
            id: itemId,
            baseData
          });
        }
      }
    }
    
    return repairable;
  };

  // Get upgradeable items (metal tools and weapons)
  const getUpgradeableItems = () => {
    const upgradeable = [];
    
    for (const [itemId, item] of Object.entries(playerData.inventory || {})) {
      if (typeof item === 'object' && item !== null) {
        const baseData = ItemData[item.key];
        if (baseData && (baseData.type === 'tool' || baseData.type === 'weapon')) {
          // For now, only allow upgrading if it's not already at max theoretical upgrade
          upgradeable.push({
            ...item,
            id: itemId,
            baseData
          });
        }
      }
    }
    
    return upgradeable;
  };

  const calculateRepairCost = (item) => {
    const durabilityLost = item.maxDurability - item.durability;
    const baseValue = item.baseData?.goldValue || 10;
    return Math.ceil((durabilityLost / item.maxDurability) * baseValue * 0.5); // 50% of proportional value
  };

  const calculateUpgradeCost = (item) => {
    const baseValue = item.baseData?.goldValue || 10;
    return baseValue * 2; // 2x base value for upgrade
  };

  const canRepairItem = (item) => {
    const cost = calculateRepairCost(item);
    return playerData.gold >= cost;
  };

  const canUpgradeItem = (item) => {
    const cost = calculateUpgradeCost(item);
    return playerData.gold >= cost;
  };

  const handleRepairItem = (item) => {
    if (!canRepairItem(item)) return;
    
    // Custom repair logic - this would integrate with the parent's action system
    const repairAction = {
      type: 'repair',
      itemId: item.id,
      cost: calculateRepairCost(item)
    };
    
    onPlayerAction('repairItem', repairAction);
  };

  const handleUpgradeItem = (item) => {
    if (!canUpgradeItem(item)) return;
    
    // Custom upgrade logic - this would integrate with the parent's action system
    const upgradeAction = {
      type: 'upgrade',
      itemId: item.id,
      cost: calculateUpgradeCost(item)
    };
    
    onPlayerAction('upgradeItem', upgradeAction);
  };

  const handleStopCrafting = () => {
    onPlayerAction('stopCrafting', { buildingKey: 'anvil' });
  };

  const handleToggleContinuous = () => {
    onPlayerAction('toggleContinuousCrafting', { buildingKey: 'anvil' });
  };

  const buildingLevel = playerData.buildings?.anvil?.upgradeLevel || 0;

  const repairableItems = getRepairableItems();
  const upgradeableItems = getUpgradeableItems();

  return (
    <div className={styles.anvilContainer}>
      {onGoBack && (
        <button 
          className={styles.backButton}
          onClick={onGoBack}
          title="Back to Home"
        >
          ✕
        </button>
      )}
      <div className={styles.anvilHeader}>
        <img src="/img/buildingItems/anvil.png" alt="Anvil" className={styles.anvilIcon} />
        <div className={styles.anvilInfo}>
          <h2>Anvil Services</h2>
          <p>Level {buildingLevel}</p>
          <p className={styles.description}>
            Item repair and enhancement services
          </p>
        </div>
      </div>

      {/* Active Crafting Progress */}
      {activeCrafting && (
        <div className={styles.craftingProgress}>
          <h3>⚒️ Currently Working</h3>
          <div className={styles.activeRecipe}>
            <div className={styles.recipeInfo}>
              <span className={styles.recipeName}>
                {activeCrafting.actionConfig.type === 'repair' ? 'Repairing Item' : 'Upgrading Item'}
              </span>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill} 
                  style={{ width: `${(activeCrafting.progress || 0) * 100}%` }}
                ></div>
              </div>
              <span className={styles.progressText}>
                {Math.floor((activeCrafting.progress || 0) * 100)}% Complete
              </span>
            </div>
            <div className={styles.craftingControls}>
              <button 
                onClick={handleToggleContinuous}
                className={`${styles.controlButton} ${activeCrafting.continuousCrafting ? styles.active : ''}`}
              >
                {activeCrafting.continuousCrafting ? '🔄 Continuous ON' : '🔄 Continuous OFF'}
              </button>
              <button 
                onClick={handleStopCrafting}
                className={`${styles.controlButton} ${styles.stopButton}`}
              >
                ⏹️ Stop
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.tabsContainer}>
        <button 
          className={`${styles.tab} ${activeTab === 'repair' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('repair')}
        >
          🔧 Repair ({repairableItems.length})
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'upgrade' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('upgrade')}
        >
          ⚡ Upgrade ({upgradeableItems.length})
        </button>
      </div>

      {activeTab === 'repair' && (
        <div className={styles.serviceSection}>
          <h3>Item Repair</h3>
          {repairableItems.length === 0 ? (
            <p className={styles.emptyMessage}>No items need repair</p>
          ) : (
            <div className={styles.itemGrid}>
              {repairableItems.map((item) => {
                const cost = calculateRepairCost(item);
                const canRepair = canRepairItem(item);
                
                return (
                  <div 
                    key={item.id} 
                    className={`${styles.itemCard} ${!canRepair ? styles.disabled : ''}`}
                  >
                    <img 
                      src={getItemImageSrc(item.key)} 
                      alt={item.baseData?.name} 
                      className={styles.itemIcon} 
                    />
                    <div className={styles.itemInfo}>
                      <h4>{item.baseData?.name}</h4>
                      <div className={styles.durabilityBar}>
                        <div className={styles.durabilityFill} 
                             style={{ width: `${(item.durability / item.maxDurability) * 100}%` }}>
                        </div>
                      </div>
                      <p>{item.durability}/{item.maxDurability} durability</p>
                      <p className={styles.cost}>Repair cost: {cost}G</p>
                    </div>
                    <button 
                      onClick={() => handleRepairItem(item)}
                      disabled={!canRepair || activeCrafting}
                      className={styles.actionButton}
                    >
                      {activeCrafting ? 'Anvil Busy' : (canRepair ? 'Repair' : 'Not enough gold')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'upgrade' && (
        <div className={styles.serviceSection}>
          <h3>Item Upgrades</h3>
          {upgradeableItems.length === 0 ? (
            <p className={styles.emptyMessage}>No upgradeable items found</p>
          ) : (
            <div className={styles.itemGrid}>
              {upgradeableItems.map((item) => {
                const cost = calculateUpgradeCost(item);
                const canUpgrade = canUpgradeItem(item);
                
                return (
                  <div 
                    key={item.id} 
                    className={`${styles.itemCard} ${!canUpgrade ? styles.disabled : ''}`}
                  >
                    <img 
                      src={getItemImageSrc(item.key)} 
                      alt={item.baseData?.name} 
                      className={styles.itemIcon} 
                    />
                    <div className={styles.itemInfo}>
                      <h4>{item.baseData?.name}</h4>
                      <p>Current: {item.damage || 'N/A'} damage</p>
                      <p>Upgrade: +10% stats</p>
                      <p className={styles.cost}>Upgrade cost: {cost}G</p>
                    </div>
                    <button 
                      onClick={() => handleUpgradeItem(item)}
                      disabled={!canUpgrade || activeCrafting}
                      className={styles.actionButton}
                    >
                      {activeCrafting ? 'Anvil Busy' : (canUpgrade ? 'Upgrade' : 'Not enough gold')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className={styles.anvilTips}>
        <h4>⚒️ Anvil Tips</h4>
        <ul>
          <li>Repair broken items to restore their functionality</li>
          <li>Upgrades permanently improve item statistics</li>
          <li>Higher anvil levels reduce service costs</li>
          <li>Keep tools in good condition for best performance</li>
        </ul>
      </div>
    </div>
  );
};

export default AnvilView; 